import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkle, PencilSimpleLine, UploadSimple, FileText, X, CheckCircle } from "@phosphor-icons/react";
import { extractErrorMessage } from "@/lib/apiError";
import PitchBuildForMe from "@/components/PitchBuildForMe";

const C = {
  bgDark: "#000", panel: "#0A2826", teal: "#0A8B85",
  tealSoft: "#7BD3CE", orange: "#F59E0B",
};

const ClinqLogo = () => (
  <div className="flex items-center gap-3">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
      <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.tealSoft }}>Pitch</span>
      <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Builder</span>
    </div>
  </div>
);

const TOTAL_STEPS = 4;

export default function PitchNew() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company_name: "", one_liner: "", industry: "",
    industry_other_desc: "",
    funder_type: "equity", stage: "Pre-revenue", country: "",
    capital_ask: "", revenue_model: "",
  });
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Creating…");
  const [mode, setMode] = useState(null); // null = chooser · "forme" | "diy"
  const [snapPrefill, setSnapPrefill] = useState(null);
  const [prefilled, setPrefilled] = useState(false);
  // Document upload step (never a gate)
  const [uploads, setUploads] = useState([]); // [{doc_id, filename, chars}]
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/pitch/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/fm/prefill").then(({ data }) => {
      const p = data?.prefill;
      if (!p) return;
      setSnapPrefill(p);
      setForm((f) => ({
        ...f,
        company_name: f.company_name || p.company_name || "",
        country: p.country || f.country,
        one_liner: f.one_liner || p.value_prop || "",
      }));
      if (p.company_name || p.value_prop) setPrefilled(true);
    }).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (step === 1 && !form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (step === 2 && !form.industry) {
      toast.error("Pick your industry — or choose Other");
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/pitch/intake/upload", fd);
        setUploads((u) => [...u, { doc_id: data.doc_id, filename: data.filename, chars: data.chars }]);
      } catch (err) {
        toast.error(`${f.name}: ${extractErrorMessage(err, "upload failed")}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const create = async () => {
    setBusy(true);
    setBusyLabel("Creating…");
    try {
      const { data } = await api.post("/pitch/decks", form);
      if (uploads.length) {
        setBusyLabel("Reading your documents…");
        try {
          const pre = await api.post(`/pitch/decks/${data.deck_id}/prefill-from-docs`, {
            doc_ids: uploads.map((u) => u.doc_id),
          });
          const n = (pre.data.filled_slides || []).length;
          if (n > 0) toast.success(`Pre-filled ${n} slide${n > 1 ? "s" : ""} from your documents`, { duration: 8000 });
          else toast.info("We couldn't find pre-fillable data in your documents — your deck is ready to build.");
        } catch {
          toast.info("We couldn't pre-fill automatically — your deck is ready to build.");
        }
      } else {
        toast.success("Pitch deck created");
      }
      navigate(`/pitch/${data.deck_id}`);
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not create deck"));
    } finally {
      setBusy(false);
    }
  };

  if (!meta) {
    return <div className="min-h-screen text-white grid place-items-center" style={{ backgroundColor: C.bgDark }}>Loading…</div>;
  }

  const tpl = meta.industry_templates?.[form.industry] || null;
  const docLists = tpl?.docs || { recommended: [], optional: [] };
  const reqSlideCount = tpl ? Object.keys(tpl.slide_requirements || {}).length : 0;

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="pitch-new-page">
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard"><ClinqLogo /></Link>
          <Link to="/dashboard" className="text-sm text-white/70 hover:text-white" data-testid="cancel-link">Cancel</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-12">
        {mode === null && (
          <div data-testid="pitch-mode-chooser">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>New pitch deck</div>
            <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              How do you want to <span style={{ color: C.teal }}>build it</span>?
            </h1>
            <p className="text-white/60 mt-3 max-w-xl">Both paths end in the same editor — you stay in control of every slide.</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <button onClick={() => setMode("forme")} data-testid="mode-build-for-me"
                className="text-left rounded-xl p-6 transition-all hover:-translate-y-1"
                style={{ backgroundColor: "rgba(10,139,133,0.12)", border: "1px solid rgba(10,139,133,0.5)" }}>
                <div className="w-10 h-10 rounded-lg grid place-items-center mb-4"
                  style={{ backgroundColor: "rgba(10,139,133,0.3)" }}>
                  <Sparkle size={20} weight="fill" style={{ color: C.tealSoft }} />
                </div>
                <div className="text-lg font-extrabold text-white">Build it for me</div>
                <div className="text-[13px] text-white/60 mt-2 leading-relaxed">
                  Answer David's short interview — share what you know, paste what you have — and he drafts
                  all 11 slides with your real numbers. You review and polish.
                </div>
                <div className="mt-4 text-[11px] font-bold uppercase tracking-widest" style={{ color: C.orange }}>
                  25 credits · ~5 min interview
                </div>
              </button>
              <button onClick={() => setMode("diy")} data-testid="mode-step-by-step"
                className="text-left rounded-xl p-6 transition-all hover:-translate-y-1"
                style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)" }}>
                <div className="w-10 h-10 rounded-lg grid place-items-center mb-4"
                  style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
                  <PencilSimpleLine size={20} weight="bold" style={{ color: "#fff" }} />
                </div>
                <div className="text-lg font-extrabold text-white">Build it step by step</div>
                <div className="text-[13px] text-white/60 mt-2 leading-relaxed">
                  The guided wizard — you write each slide in order with AI help, funder-readiness checks
                  and design templates along the way.
                </div>
                <div className="mt-4 text-[11px] font-bold uppercase tracking-widest text-white/40">
                  Free to build · pay only on export
                </div>
              </button>
            </div>
          </div>
        )}

        {mode === "forme" && (
          <PitchBuildForMe meta={meta} prefill={snapPrefill} onBack={() => setMode(null)} />
        )}

        {mode === "diy" && (<>
        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-10">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
            <React.Fragment key={n}>
              <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold ${step >= n ? "" : "text-white/40"}`}
                style={{ backgroundColor: step >= n ? C.teal : "rgba(255,255,255,0.08)" }}>
                {n}
              </div>
              {n < TOTAL_STEPS && <div className="flex-1 h-px" style={{ backgroundColor: step > n ? C.teal : "rgba(255,255,255,0.1)" }}></div>}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Business basics */}
        {step === 1 && (
          <div data-testid="step-1">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>Step 1 — About the business</div>
            <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              Tell us what you&apos;re <span style={{ color: C.teal }}>raising for</span>.
            </h1>
            <p className="text-white/60 mt-3 max-w-xl">Everything below tunes the deck — pillar weights, mandatory data, AI tone.</p>
            {prefilled && (
              <div className="mt-4 inline-block rounded-md px-4 py-2 text-xs font-semibold" data-testid="pitch-prefill-note"
                style={{ backgroundColor: "rgba(10,139,133,0.15)", border: "1px solid rgba(10,139,133,0.4)", color: "#7BD3CE" }}>
                ✓ Pre-filled from your Business Snapshot — adjust anything that's changed.
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Company name"
                required
                hint={(() => {
                  const n = (form.company_name || "").length;
                  if (n === 0) return "Keep it short — appears at 54pt on the cover slide";
                  const tone = n <= 24 ? "text-emerald-400/80"
                    : n <= 40 ? "text-white/50"
                      : "text-amber-400";
                  const msg = n <= 24 ? "Fits comfortably on the cover"
                    : n <= 40 ? "OK — may wrap on narrower screens"
                      : "Long name — the cover will feel cramped";
                  return (
                    <span className={`font-mono-num tabular-nums ${tone}`}
                          data-testid="company-name-counter">
                      {n} chars · {msg}
                    </span>
                  );
                })()}
              >
                <input type="text" value={form.company_name} onChange={(e) => update("company_name", e.target.value)}
                  data-testid="company-name-input" placeholder="Acme Logistics"
                  className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
              </Field>
              <Field label="Country / market">
                <input type="text" value={form.country} onChange={(e) => update("country", e.target.value)}
                  data-testid="country-input" placeholder="Kenya"
                  className="w-full px-3 py-2.5 rounded-md text-sm text-white focus:outline-none focus:border-[#0A8B85]"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
              </Field>
              <Field label="One-liner" hint="Plain English. What you do, for whom, where.">
                <input type="text" value={form.one_liner} onChange={(e) => update("one_liner", e.target.value)}
                  data-testid="one-liner-input"
                  placeholder="Last-mile cold-chain logistics for African pharma distributors."
                  className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
              </Field>
              <Field label="What stage is your business?" hint="Funders weigh decks differently before vs after revenue.">
                <div className="grid grid-cols-2 gap-2" data-testid="stage-segmented">
                  {[
                    { v: "Pre-revenue",  label: "Pre-revenue",  sub: "No paying customers yet" },
                    { v: "Post-revenue", label: "Post-revenue", sub: "Generating revenue" },
                  ].map((opt) => {
                    const selected = form.stage === opt.v;
                    return (
                      <button key={opt.v} type="button" onClick={() => update("stage", opt.v)}
                        data-testid={`stage-${opt.v.toLowerCase()}`}
                        className="text-left px-3.5 py-3 rounded-md border-2 transition-all"
                        style={{
                          backgroundColor: selected ? "rgba(10, 139, 133, 0.12)" : "rgba(0,0,0,0.3)",
                          borderColor: selected ? C.teal : "rgba(255,255,255,0.12)",
                        }}>
                        <div className="text-[13px] font-bold" style={{ color: selected ? C.tealSoft : "#fff" }}>{opt.label}</div>
                        <div className="text-[11px] text-white/50 mt-0.5">{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </div>
        )}

        {/* STEP 2: Industry selection (Clinq industry templates · CRS v2.2) */}
        {step === 2 && (
          <div data-testid="step-2">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>Step 2 — Pick your industry</div>
            <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              What industry is your <span style={{ color: C.teal }}>business in</span>?
            </h1>
            <p className="text-white/60 mt-3 max-w-2xl">
              Your industry selects the template — the exact data points, proof standards and mandatory items funders expect within each slide.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="industry-grid">
              {(meta.industries || []).map((ind) => {
                const selected = form.industry === ind.id;
                const t = meta.industry_templates?.[ind.id];
                const tunedSlides = t ? Object.keys(t.slide_requirements || {}).length : 0;
                const isOther = ind.id === "other";
                return (
                  <button key={ind.id} onClick={() => update("industry", ind.id)}
                    data-testid={`industry-${ind.id}`}
                    className="text-left p-5 rounded-xl border-2 transition-all hover:-translate-y-0.5"
                    style={{
                      backgroundColor: selected ? "rgba(44, 197, 168, 0.08)" : "rgba(255,255,255,0.025)",
                      borderColor: selected ? C.teal : "rgba(255,255,255,0.08)",
                    }}>
                    <div className="font-extrabold text-base">{isOther ? "Other (I'll describe my industry)" : ind.name}</div>
                    {tunedSlides > 0 ? (
                      <div className="mt-3 text-[11px] uppercase tracking-widest font-semibold" style={{ color: C.orange }}>
                        Tuned template · {tunedSlides} slides with industry must-haves
                      </div>
                    ) : (
                      <div className="mt-3 text-[11px] text-white/40">General template</div>
                    )}
                  </button>
                );
              })}
            </div>

            {form.industry === "other" && (
              <div className="mt-6 max-w-xl" data-testid="industry-other-panel">
                <Field label="Briefly describe your industry">
                  <input type="text" value={form.industry_other_desc}
                    onChange={(e) => update("industry_other_desc", e.target.value)}
                    data-testid="industry-other-desc" placeholder="e.g. Waste recycling marketplace"
                    className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                    style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
                </Field>
                <div className="mt-3 text-[12px] text-white/50 leading-relaxed rounded-md px-4 py-3"
                  style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  {meta.generic_template_note}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Document upload (never a gate) */}
        {step === 3 && (
          <div data-testid="step-3-docs">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>Step 3 — Your documents</div>
            <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              Upload any existing documents and we&apos;ll <span style={{ color: C.teal }}>pre-fill your deck</span> where we can.
            </h1>
            <p className="text-white/60 mt-3 max-w-2xl">
              Based on your {tpl?.name || "industry"} template, these documents help most. Nothing here is required — you can skip and build from scratch.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-xl p-5 border" style={{ backgroundColor: "rgba(10,139,133,0.06)", borderColor: "rgba(10,139,133,0.3)" }}>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: C.tealSoft }}>Recommended</div>
                {docLists.recommended.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 text-[13px] text-white/80 mt-2" data-testid={`doc-rec-${i}`}>
                    <span className="mt-0.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                      style={{ backgroundColor: "rgba(10,139,133,0.25)", color: C.tealSoft }}>REC</span>
                    <span>{d}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-5 border" style={{ backgroundColor: "rgba(255,255,255,0.025)", borderColor: "rgba(255,255,255,0.1)" }}>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3 text-white/45">Optional</div>
                {docLists.optional.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 text-[13px] text-white/60 mt-2" data-testid={`doc-opt-${i}`}>
                    <span className="mt-0.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 bg-white/10 text-white/50">OPT</span>
                    <span>{d}</span>
                  </div>
                ))}
              </div>
            </div>

            <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.pptx,.xlsx,.txt"
              onChange={(e) => uploadFiles(e.target.files)} style={{ display: "none" }} data-testid="doc-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="doc-upload-btn"
              className="mt-6 w-full rounded-xl border-2 border-dashed p-8 text-center transition-colors hover:border-[#0A8B85] disabled:opacity-50"
              style={{ borderColor: "rgba(123,211,206,0.3)", backgroundColor: "rgba(0,0,0,0.25)" }}>
              <UploadSimple size={24} className="mx-auto mb-2" style={{ color: C.tealSoft }} />
              <div className="text-sm font-bold text-white">{uploading ? "Uploading…" : "Click to upload documents"}</div>
              <div className="text-[11px] text-white/45 mt-1">PDF, DOCX, PPTX, XLSX or TXT · max 15 MB each · we extract the data, your files stay private</div>
            </button>

            {uploads.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="uploaded-docs-list">
                {uploads.map((u) => (
                  <div key={u.doc_id} className="flex items-center gap-3 rounded-lg px-4 py-2.5 border"
                    style={{ backgroundColor: "rgba(10,139,133,0.08)", borderColor: "rgba(10,139,133,0.3)" }}>
                    <FileText size={16} style={{ color: C.tealSoft }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate">{u.filename}</div>
                      <div className="text-[10px] text-white/40">{Math.round(u.chars / 1000)}k characters extracted</div>
                    </div>
                    <CheckCircle size={16} weight="fill" style={{ color: C.tealSoft }} />
                    <button onClick={() => setUploads((arr) => arr.filter((x) => x.doc_id !== u.doc_id))}
                      className="text-white/40 hover:text-white" data-testid={`doc-remove-${u.doc_id}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button onClick={next} className="mt-5 text-sm font-semibold text-white/60 hover:text-white underline underline-offset-4"
              data-testid="skip-docs-btn">
              Skip for now — I&apos;ll build from scratch
            </button>
          </div>
        )}

        {/* STEP 4: Funder type — simplified to debt / equity / both */}
        {step === 4 && (
          <div data-testid="step-4">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>Step 4 — Who are you raising from?</div>
            <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              What type of funder are you <span style={{ color: C.teal }}>looking for</span>?
            </h1>
            <p className="text-white/60 mt-3 max-w-2xl">
              The two main groups read your deck differently. Pick the one closest to your raise — or both if you&apos;re still exploring.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="funder-grid">
              {meta.funder_types.slice(0, 3).map((ft) => {
                const selected = form.funder_type === ft.id;
                return (
                  <button key={ft.id} onClick={() => update("funder_type", ft.id)}
                    data-testid={`funder-${ft.id}`}
                    className="text-left p-6 rounded-2xl border-2 transition-all hover:-translate-y-1 relative overflow-hidden group"
                    style={{
                      backgroundColor: selected ? "rgba(44, 197, 168, 0.08)" : "rgba(255,255,255,0.025)",
                      borderColor: selected ? C.teal : "rgba(255,255,255,0.08)",
                    }}>
                    <div className="absolute -right-12 -top-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-3xl"
                      style={{ backgroundColor: C.teal }}></div>
                    <div className="relative">
                      <div className="font-extrabold text-xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>{ft.name}</div>
                      <p className="text-sm text-white/65 mt-3 leading-relaxed">{ft.desc}</p>
                      {ft.examples && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="text-[9px] uppercase tracking-[0.22em] font-semibold mb-1.5" style={{ color: C.tealSoft }}>For example</div>
                          <div className="text-xs text-white/55">{ft.examples}</div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-12 pt-6 border-t border-white/10">
          {step === TOTAL_STEPS && (
            <div className="text-[11px] text-white/40 mb-4" data-testid="pitch-cost-note">
              Creating and editing your deck is <span className="text-white/70 font-semibold">free</span> — credits are only spent when you generate the downloadable PPTX/PDF at the end.
            </div>
          )}
          <div className="flex items-center justify-between">
          <button onClick={prev} disabled={step === 1 || busy} data-testid="prev-btn"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white disabled:opacity-30 disabled:hover:text-white/70 transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          {step < TOTAL_STEPS ? (
            <button onClick={next} data-testid="next-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold text-sm transition-colors"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={create} disabled={busy} data-testid="create-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold text-sm transition-colors disabled:opacity-60"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              {busy ? busyLabel : "Create deck"} <ArrowRight size={14} />
            </button>
          )}
          </div>
        </div>
        </>)}
      </main>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1.5">
        {label} {required && <span style={{ color: C.orange }}>*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-white/40 mt-1.5">{hint}</div>}
    </label>
  );
}
